import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Divider, TextField } from "@mui/material";
import CustomDialog from "../../../../components/Dialog/CustomDialog";
import { postRequest } from "../../../../Apis";
import { campaignEndpoints } from "../../../../utils/constants/httpConstants";
import parse from "html-react-parser";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../../../utils/constants";
import { decompressString } from "../../../../utils/helpers";
import pako from "pako";
import IconComponent from "../../../../components/Icon/IconComponent";
import { ICON_LABELS } from "../../../../utils/constants/UI/iconLabels";
import { BRAND } from "../../../../utils/constants/colors";
import Button from "../../../../components/Buttons/Button";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";
import useRegeneratingSteps from "../../../../hooks/useRegeneratingSteps";

// REGENERATE HIDDEN PLATFORM-WIDE (2026-08-14, owner call). Kept as a single
// flag rather than deleting the feature so it is a one-line switch back on.
// Pairs with HIDE_REGENERATE_CAMPAIGN in CampaignScreen.js (whole-campaign one).
const HIDE_REGENERATE_EMAIL = true;

const ViewAiContentDialog = ({
  open,
  onClose,
  prospect,
  step, // The specific step object
  stepNumber, // NEW: The step number (1, 2, 3...) passed from parent
  campaignId,
  sequenceId,
  email,
  navigate,
}) => {
  const [loading, setLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStepInfo, setEditingStepInfo] = useState({
    id: null,
    type: null,
  });
  const [saving, setSaving] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [editedNotes, setEditedNotes] = useState("");
  const editorRef = useRef(null);

  const fetchAiContent = async () => {
    if (!open) return;

    // 1. Extract IDs
    const record_id = prospect?.record_id || prospect?._id;

    // 2. Validation
    // if (!record_id) {
    //   console.error("Missing record_id");
    //   return;
    // }

    setLoading(true);
    try {
      const payload = {
        campaign_id: campaignId,
        // record_id: record_id,
        sequence_id: sequenceId,
        email: email,
      };

      const response = await postRequest(
        navigate,
        campaignEndpoints.viewAiGeneratedcontent,
        payload,
      );

      if (response?.data?.data) {
        setAiData(response.data.data);
      }
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to fetch content",
      );
      setAiData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setAiData(null);
      fetchAiContent();
    }
  }, [open, prospect, step, campaignId, sequenceId, navigate]);

  // Helper to detect if content looks compressed (base64-like pattern)
  const isCompressed = (data) => {
    if (!data || typeof data !== "string") return false;
    // Check if it looks like base64 compressed data (no spaces, special chars like =)
    // and doesn't contain common readable patterns
    return (
      /^[A-Za-z0-9+/=]+$/.test(data) && data.length > 50 && !data.includes(" ")
    );
  };

  const escapeHtml = (content = "") =>
    content
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const looksLikeHtml = (content = "") => /<\/?[a-z][\s\S]*>/i.test(content);

  const isSafeUrl = (url = "") => /^https?:\/\/[^\s<]+$/i.test(url);

  const buildAnchorTag = (url, label) => {
    if (!isSafeUrl(url)) return label;

    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  };

  // Helper to handle decompression and URL decoding
  // A body that is still percent-encoded (encodeURIComponent output) but NOT
  // compressed used to be handed straight to the renderer, so the panel showed
  // "Hi%20jia%20test%2C%3Cbr%3E..." instead of the email. Bodies are written by
  // several paths and only some add the encode layer, so decode whenever the
  // string still carries %XX escapes. Never throw: a bare '%' (e.g. "20% off")
  // makes decodeURIComponent fail, and the raw text is the right answer then.
  // Deliberately narrow: a WHOLE encoded body has its angle brackets escaped
  // (%3Cbr%3E), or — for a plain-text email — its spaces escaped and no literal
  // space left. A normal body that merely carries an encoded query param in a
  // tracking link ("?d=abc%3D%3D") matches neither and is left alone, so we
  // never rewrite a working URL.
  const looksPercentEncoded = (s = "") =>
    /%3C|%3E/i.test(s) || (/%20/.test(s) && !s.includes(" "));
  const safeDecode = (s) => {
    if (!looksPercentEncoded(s)) return s;
    try {
      return decodeURIComponent(s);
    } catch (e) {
      return s;
    }
  };

  const getDecompressedContent = (data) => {
    if (!data) return "";

    // If content looks like plain text (not compressed), handle it directly —
    // but still strip a leftover percent-encoding layer if one is present.
    if (!isCompressed(data)) {
      return safeDecode(data);
    }

    // Otherwise, try to decompress
    try {
      let decompressed = decompressString(data);

      if (!decompressed) {
        try {
          const compressedUint8Array = Uint8Array.from(atob(data), (c) =>
            c.charCodeAt(0),
          );
          const decompressedUint8Array = pako.inflate(compressedUint8Array);
          decompressed = new TextDecoder().decode(decompressedUint8Array);
        } catch (manualError) {
          // If decompression fails, treat as plain text
          return data;
        }
      }

      return safeDecode(decompressed);
    } catch (e) {
      // Fallback: treat as plain text
      return data;
    }
  };

  const formatContentForHtml = (data) => {
    const content = getDecompressedContent(data);

    if (!content) return "";

    if (looksLikeHtml(content)) {
      return content.replace(/\n/g, "<br>");
    }

    const markdownTokens = [];
    let html = escapeHtml(content).replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi,
      (_, label, url) => {
        const token = `__MARKDOWN_LINK_${markdownTokens.length}__`;
        markdownTokens.push(
          buildAnchorTag(url, escapeHtml(label.trim()) || escapeHtml(url)),
        );
        return token;
      },
    );

    const urlTokens = [];
    html = html.replace(/(^|[\s(>])((https?:\/\/[^\s<]+))/gi, (match, prefix, url) => {
      const normalizedUrl = url.replace(/[),.;!?]+$/, "");
      const trailingText = url.slice(normalizedUrl.length);
      const token = `__URL_LINK_${urlTokens.length}__`;
      urlTokens.push(buildAnchorTag(normalizedUrl, escapeHtml(normalizedUrl)));

      return `${prefix}${token}${trailingText}`;
    });

    html = html.replace(/\n/g, "<br>");

    markdownTokens.forEach((tokenValue, index) => {
      html = html.replace(`__MARKDOWN_LINK_${index}__`, tokenValue);
    });

    urlTokens.forEach((tokenValue, index) => {
      html = html.replace(`__URL_LINK_${index}__`, tokenValue);
    });

    return html;
  };

  // REGENERATE (2026-08-13): re-runs AI personalization for THIS contact+step
  // via /regenerate-step-content — same prompt, same step instruction, same
  // model as the campaign run. Costs 1 credit (backend refunds on failure).
  // QUEUED / already-sent rows come back with a typed code -> themed popup.
  const [regenerating, setRegenerating] = useState(false);
  const [blockedPopup, setBlockedPopup] = useState({ open: false, message: "" });
  // Firebase mirror of "this step is regenerating" — survives a closed drawer
  // or page refresh (the local `regenerating` flag only covers this mount).
  const regenKeys = useRegeneratingSteps(open ? campaignId : null);
  const fbKey = step?.cam_step_id
    ? `${step.cam_step_id}|${String(email || "").toLowerCase()}`
    : null;
  const fbRegenerating = fbKey ? regenKeys.has(fbKey) : false;
  const showRegenerating = regenerating || fbRegenerating;
  // A step whose email already WENT OUT gets no Regenerate at all (Aditya):
  // the engine deleted its scheduled row, so there is nothing to regenerate —
  // hiding beats offering a button that can only pop the "already sent" error.
  // Status arrives in raw casing ("sent", "QUEUED", "delivered"…), so compare
  // lowercased — same reason CampaignContactsScreen normalizes it.
  const SENT_STATUSES = [
    "sent",
    "delivered",
    "opened",
    "replied",
    "bounced",
    "unsubscribed",
    "lead",
  ];
  const canRegenerate =
    !HIDE_REGENERATE_EMAIL &&
    !SENT_STATUSES.includes(String(step?.status || "").toLowerCase());
  // when the Firebase flag clears (regeneration finished server-side while we
  // were watching), pull the fresh content into the panel
  const prevFbRegen = useRef(false);
  useEffect(() => {
    if (prevFbRegen.current && !fbRegenerating && !regenerating) {
      fetchAiContent();
    }
    prevFbRegen.current = fbRegenerating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbRegenerating]);
  const handleRegenerate = async (activeEmail) => {
    if (showRegenerating) return;
    const stepId = activeEmail?.step_id || step?.step_id || step?._id;
    if (!stepId) {
      EventEmitter.emit(EMITTER_ERROR, "Could not identify this email step.");
      return;
    }
    setRegenerating(true);
    try {
      // step_id here is the CAM STEP id (shared by every contact on that
      // step) — the contact's email is what picks the right scheduled row
      await postRequest(navigate, campaignEndpoints.regenerateStepContent, {
        campaign_id: campaignId,
        step_id: stepId,
        email,
      });
      await fetchAiContent(); // panel refreshes with the fresh email
    } catch (err) {
      const code = err?.data?.code;
      const message =
        err?.data?.message || "Regeneration failed. Please try again.";
      if (code === "IN_QUEUE" || code === "ALREADY_SENT") {
        // Aditya rule: queued/sent emails can't be regenerated — themed popup
        setBlockedPopup({ open: true, message });
      } else {
        EventEmitter.emit(EMITTER_ERROR, message);
      }
    } finally {
      setRegenerating(false);
    }
  };

  // Handle edit mode
  const handleEditClick = (type, data) => {
    setEditingStepInfo({ id: data.step_id, type });
    if (type === "email") {
      const subject = data.subject?.replace(/^Subject:\s*/i, "");
      const body = formatContentForHtml(data.body);
      setEditedSubject(subject);
      setEditedBody(body || "");
    } else if (type === "call" || type === "todo") {
      const notes = formatContentForHtml(data.notes);
      setEditedTitle(data.title);
      setEditedNotes(notes || "");
    }
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingStepInfo({ id: null, type: null });
    setEditedSubject("");
    setEditedBody("");
    setEditedTitle("");
    setEditedNotes("");
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      let payload = {
        campaign_id: campaignId,
        sequence_id: sequenceId,
        email: email,
        step_id: editingStepInfo.id,
        step_type: editingStepInfo.type,
      };

      if (editingStepInfo.type === "email") {
        payload.subject = editedSubject;
        payload.body = editorRef.current?.innerHTML || "";
      } else {
        payload.title = editedTitle;
        payload.notes = editorRef.current?.innerHTML || "";
      }

      const response = await postRequest(
        navigate,
        campaignEndpoints.editaigeneratedcontent,
        payload,
      );

      if (response?.data?.success) {
        setIsEditing(false);
        setEditingStepInfo({ id: null, type: null });
        fetchAiContent();
      }
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to update content",
      );
    } finally {
      setSaving(false);
    }
  };

  // Helper function to generate name from email
  const generateNameFromEmail = (email) => {
    if (!email) return "";
    const [localPart] = email.split("@");
    return localPart
      .split(/[._-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get the contact display name
  const getContactDisplayName = () => {
    if (!prospect) return "AI Generated Content";
    return (
      prospect.full_name ||
      generateNameFromEmail(prospect.email) ||
      prospect.email ||
      "AI Generated Content"
    );
  };

  const selectedStepId = step?.step_id || step?._id || null;
  const selectedStepNumber = Number(step?.step_number || stepNumber || 0);

  const getActiveContentByStep = (items = []) => {
    if (!Array.isArray(items) || !items.length) return null;
    if (selectedStepId) {
      const matchedById = items.find((item) => item?.step_id === selectedStepId);
      if (matchedById) return matchedById;
    }
    return items.find(
      (item) => Number(item?.step_number || 0) === selectedStepNumber,
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      width="40vw"
      header={{
        heading: {
          label: getContactDisplayName(),
        },
      }}
    >
      <Box>
        {loading ? (
          <Box p={2}>
            {/* Email Skeleton Structure */}
            <Box mb={3}>
              <SkeletonLoader
                type="text"
                skeletonWidth="30%"
                skeletonHeight={24}
                stackHeight="auto"
              />
              <Box mt={2}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="60%"
                  skeletonHeight={20}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={2}>
                <SkeletonLoader
                  type="rectangular"
                  skeletonWidth="100%"
                  skeletonHeight={1}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={2}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="100%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="95%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="90%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="85%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="92%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="85%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="95%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="80%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="95%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="80%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="95%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="80%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : aiData?.content ? (
          <Box>
            {/* --- EMAIL LOGIC (Match by step_number) --- */}
            {(() => {
              const activeEmail = getActiveContentByStep(aiData.content.emails);
              if (activeEmail) {
                return (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Email Content
                      </Typography>
                      {!isEditing && step?.status !== "Delivered" && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            onClick={() => handleEditClick("email", activeEmail)}
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <IconComponent
                              iconLabel={ICON_LABELS.EDIT}
                              color={BRAND.PRIMARY}
                              fontSize={"20px"}
                            />
                          </Box>
                          {/* REGENERATE (2026-08-13): after the edit pencil,
                              styled to match it (brand icon + small label, no
                              box). Spins its icon while the new email is
                              being generated. Hidden entirely once the email
                              has gone out (Sent/Delivered/Opened/…). */}
                          {canRegenerate && (
                          <Box
                            onClick={() => handleRegenerate(activeEmail)}
                            sx={{
                              cursor: showRegenerating ? "default" : "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              opacity: showRegenerating ? 0.6 : 1,
                              "@keyframes regenSpin": {
                                from: { transform: "rotate(0deg)" },
                                to: { transform: "rotate(360deg)" },
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                animation: showRegenerating
                                  ? "regenSpin 1s linear infinite"
                                  : "none",
                              }}
                            >
                              <IconComponent
                                iconLabel={ICON_LABELS.REFRESH}
                                color={BRAND.PRIMARY}
                                fontSize={"20px"}
                              />
                            </Box>
                            <Typography
                              sx={{
                                color: BRAND.PRIMARY,
                                fontSize: "13px",
                                fontWeight: 600,
                                lineHeight: 1,
                                userSelect: "none",
                              }}
                            >
                              {showRegenerating ? "Regenerating…" : "Regenerate"}
                            </Typography>
                          </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Subject"
                          value={editedSubject}
                          onChange={(e) => setEditedSubject(e.target.value)}
                          sx={{ mb: 2 }}
                          size="small"
                        />
                        <Box
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning
                          dangerouslySetInnerHTML={{ __html: editedBody }}
                          sx={{
                            border: "1px solid rgba(0, 0, 0, 0.23)",
                            borderRadius: "4px",
                            padding: "12px",
                            minHeight: "200px",
                            maxHeight: "400px",
                            overflowY: "auto",
                            outline: "none",
                            fontSize: "14px",
                            lineHeight: 1.6,
                            "&:focus": {
                              borderColor: "#1976d2",
                              borderWidth: "2px",
                            },
                            "& a": {
                              color: "#1976d2",
                              textDecoration: "underline",
                            },
                          }}
                        />
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="flex-end"
                          mt={2}
                        >
                          {/* CUSTOM BUTTON USAGE: CANCEL */}
                          <Button
                            variant="ghost"
                            size="sm36"
                            label="Cancel"
                            onClick={handleCancelEdit}
                          />
                          {/* CUSTOM BUTTON USAGE: SAVE */}
                          <Button
                            variant="primary"
                            size="sm36"
                            label="Save"
                            onClick={handleSaveEdit}
                            loading={saving}
                            disabled={saving}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Subject:{" "}
                          {getDecompressedContent(activeEmail.subject)?.replace(
                            /^Subject:\s*/i,
                            "",
                          )}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box className="email-body-wrapper">
                          {parse(formatContentForHtml(activeEmail.body))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              }
              return null;
            })()}

            {/* --- CALL LOGIC (Match by step_number) --- */}
            {(() => {
              const activeCall = getActiveContentByStep(aiData.content.calls);
              if (activeCall) {
                return (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Call Script
                      </Typography>
                      {!isEditing && step?.status !== "Delivered" && (
                        <Box
                          onClick={() => handleEditClick("call", activeCall)}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <IconComponent
                            iconLabel={ICON_LABELS.EDIT}
                            color={BRAND.PRIMARY}
                            fontSize={"20px"}
                          />
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Title"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          sx={{ mb: 2 }}
                          size="small"
                        />
                        <Box
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning
                          dangerouslySetInnerHTML={{ __html: editedNotes }}
                          sx={{
                            border: "1px solid rgba(0, 0, 0, 0.23)",
                            borderRadius: "4px",
                            padding: "12px",
                            minHeight: "200px",
                            maxHeight: "400px",
                            overflowY: "auto",
                            outline: "none",
                            fontSize: "14px",
                            lineHeight: 1.6,
                            "&:focus": {
                              borderColor: "#1976d2",
                              borderWidth: "2px",
                            },
                            "& a": {
                              color: "#1976d2",
                              textDecoration: "underline",
                            },
                          }}
                        />
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="flex-end"
                          mt={2}
                        >
                          {/* CUSTOM BUTTON USAGE: CANCEL */}
                          <Button
                            variant="ghost"
                            size="sm36"
                            label="Cancel"
                            onClick={handleCancelEdit}
                          />
                          {/* CUSTOM BUTTON USAGE: SAVE */}
                          <Button
                            variant="primary"
                            size="sm36"
                            label="Save"
                            onClick={handleSaveEdit}
                            loading={saving}
                            disabled={saving}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "600", mb: 1 }}
                        >
                          {activeCall.title}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box className="call-notes-wrapper">
                          {parse(formatContentForHtml(activeCall.notes))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              }
              return null;
            })()}

            {/* --- TODO LOGIC (Match by step_number) --- */}
            {(() => {
              const activeTodo = getActiveContentByStep(aiData.content.todos);
              if (activeTodo) {
                return (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Todo Task
                      </Typography>
                      {!isEditing && step?.status !== "Delivered" && (
                        <Box
                          onClick={() => handleEditClick("todo", activeTodo)}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <IconComponent
                            iconLabel={ICON_LABELS.EDIT}
                            color={BRAND.PRIMARY}
                            fontSize={"20px"}
                          />
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Title"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          sx={{ mb: 2 }}
                          size="small"
                        />
                        <Box
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning
                          dangerouslySetInnerHTML={{ __html: editedNotes }}
                          sx={{
                            border: "1px solid rgba(0, 0, 0, 0.23)",
                            borderRadius: "4px",
                            padding: "12px",
                            minHeight: "200px",
                            maxHeight: "400px",
                            overflowY: "auto",
                            outline: "none",
                            fontSize: "14px",
                            lineHeight: 1.6,
                            "&:focus": {
                              borderColor: "#1976d2",
                              borderWidth: "2px",
                            },
                            "& a": {
                              color: "#1976d2",
                              textDecoration: "underline",
                            },
                          }}
                        />
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="flex-end"
                          mt={2}
                        >
                          {/* CUSTOM BUTTON USAGE: CANCEL */}
                          <Button
                            variant="ghost"
                            size="sm36"
                            label="Cancel"
                            onClick={handleCancelEdit}
                          />
                          {/* CUSTOM BUTTON USAGE: SAVE */}
                          <Button
                            variant="primary"
                            size="sm36"
                            label="Save"
                            onClick={handleSaveEdit}
                            loading={saving}
                            disabled={saving}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "600", mb: 1 }}
                        >
                          {activeTodo.title}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box className="todo-notes-wrapper">
                          {parse(formatContentForHtml(activeTodo.notes))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              }
              return null;
            })()}

            {/* --- FALLBACK --- */}
            {(() => {
              const hasEmail = !!getActiveContentByStep(aiData.content.emails);
              const hasCall = !!getActiveContentByStep(aiData.content.calls);
              const hasTodo = !!getActiveContentByStep(aiData.content.todos);

              if (!hasEmail && !hasCall && !hasTodo) {
                return (
                  <Typography textAlign="center" color="textSecondary" mt={2}>
                    No AI content found for this specific step.
                  </Typography>
                );
              }
              return null;
            })()}
          </Box>
        ) : (
          <Typography>No content available.</Typography>
        )}
      </Box>

      {/* REGENERATE blocked popup (Aditya): queued / already-sent emails can't
          be regenerated — same CustomDialog chrome as the rest of the app. */}
      <CustomDialog
        open={blockedPopup.open}
        onClose={() => setBlockedPopup({ open: false, message: "" })}
        width="420px"
        header={{
          heading: {
            label: "Can't regenerate",
          },
        }}
      >
        <Box p={1}>
          <Typography sx={{ fontSize: "14px", lineHeight: 1.6 }}>
            {blockedPopup.message}
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="primary"
              size="sm36"
              label="Okay"
              onClick={() => setBlockedPopup({ open: false, message: "" })}
            />
          </Box>
        </Box>
      </CustomDialog>
    </CustomDialog>
  );
};

export default ViewAiContentDialog;
